import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { ENDPOINTS } from '../helpers/api/apiConfig';

export interface LatestMeasurementSummary {
    areaId: number;
    areaName?: string | null;
    range: [Dayjs, Dayjs] | null;
    count: number;
    latestAt: Dayjs | null;
    error?: string | null;
}

interface UseLatestMeasurementSummariesOptions {
    enabled?: boolean;
}

const useLatestMeasurementSummaries = (
    areas: Array<{ id: number; name?: string | null }>,
    options: UseLatestMeasurementSummariesOptions = {}
) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [summaries, setSummaries] = useState<LatestMeasurementSummary[]>([]);

    const targetAreas = useMemo(() => {
        if (!areas || areas.length === 0) return [];
        const seen = new Set<number>();
        return areas.filter((area) => {
            if (!area || typeof area.id !== 'number') return false;
            if (seen.has(area.id)) return false;
            seen.add(area.id);
            return true;
        });
    }, [areas]);

    const areaKey = useMemo(() => targetAreas.map((area) => area.id).join(','), [targetAreas]);
    const targetAreasRef = useRef(targetAreas);

    useEffect(() => {
        targetAreasRef.current = targetAreas;
    }, [targetAreas]);

    useEffect(() => {
        if (options.enabled === false) {
            setSummaries([]);
            setError(null);
            setLoading(false);
            return;
        }

        let isMounted = true;
        const controller = new AbortController();

        const loadLatestDay = async () => {
            const resolvedAreas = targetAreasRef.current;
            if (resolvedAreas.length === 0) {
                if (isMounted) {
                    setSummaries([]);
                    setError(null);
                    setLoading(false);
                }
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const results = await Promise.all(
                    resolvedAreas.map(async (area) => {
                        try {
                            const latestRes = await axios.get(ENDPOINTS.MEASUREMENTS_V2, {
                                params: {
                                    area_id: area.id,
                                    limit: 1,
                                    offset: 0,
                                },
                                withCredentials: true,
                                signal: controller.signal,
                            });

                            const latestData = latestRes.data?.data || latestRes.data || [];
                            const latestMeasurement = Array.isArray(latestData) ? latestData[0] : null;
                            const latestTimestamp = latestMeasurement?.measuredAt || latestMeasurement?.createdAt;

                            if (!latestTimestamp) {
                                return {
                                    areaId: area.id,
                                    areaName: area.name,
                                    range: null,
                                    count: 0,
                                    latestAt: null,
                                } as LatestMeasurementSummary;
                            }

                            const latestDay = dayjs(latestTimestamp);
                            const dayStart = latestDay.startOf('day');
                            const dayEnd = latestDay.endOf('day');

                            const countRes = await axios.get(ENDPOINTS.MEASUREMENTS_V2, {
                                params: {
                                    area_id: area.id,
                                    from_date: dayStart.toISOString(),
                                    to_date: dayEnd.toISOString(),
                                    limit: 1,
                                    offset: 0,
                                },
                                withCredentials: true,
                                signal: controller.signal,
                            });

                            const totalCount = countRes.data?.total ?? (countRes.data?.data?.length ?? 0);

                            return {
                                areaId: area.id,
                                areaName: area.name,
                                range: [dayStart, dayEnd] as [Dayjs, Dayjs],
                                count: totalCount,
                                latestAt: latestDay,
                            } as LatestMeasurementSummary;
                        } catch (innerErr: any) {
                            if (axios.isCancel(innerErr) || innerErr.name === 'CanceledError') {
                                return null;
                            }
                            return {
                                areaId: area.id,
                                areaName: area.name,
                                range: null,
                                count: 0,
                                latestAt: null,
                                error: 'Failed to load latest data.',
                            } as LatestMeasurementSummary;
                        }
                    })
                );

                if (isMounted) {
                    setSummaries(results.filter((r): r is LatestMeasurementSummary => r !== null));
                }
            } catch (err: any) {
                if (axios.isCancel(err) || err.name === 'CanceledError') {
                    return;
                }
                if (isMounted) {
                    setError('Could not load latest day data.');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        void loadLatestDay();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [areaKey, options.enabled]);

    return { loading, error, summaries };
};

export default useLatestMeasurementSummaries;
